import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LockKeyhole, ArrowRight } from "lucide-react";

const LOGIN_BG = "/login-bg.jpg";
const FLOWLINE_LOGO = "/flowline.png";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(user.role === "third_party" ? "/third-party" : "/dashboard", { replace: true });
    }
    api.get("/auth/demo-accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "third_party" ? "/third-party" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const quickFill = (acc) => {
    setUsername(acc.username);
    setPassword(acc.password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left — form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 grid-bg">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <img src={FLOWLINE_LOGO} alt="Flowline" className="h-12 w-12 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-extrabold text-xl tracking-tighter text-zinc-950">
                Flowline
              </span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                Operations Platform · by Soubor
              </span>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-zinc-950 mb-2">
            Sign in.
          </h1>
          <p className="text-sm text-zinc-600 mb-8">
            One clear version of the truth — from receipt to dispatch.
          </p>          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-wide text-zinc-500">
                Username
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="mt-1.5 h-11"
                data-testid="login-username-input"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-zinc-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11"
                data-testid="login-password-input"
                autoComplete="current-password"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold"
              data-testid="login-submit-button"
            >
              <LockKeyhole className="h-4 w-4 mr-2" />
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </form>

          {accounts.length > 0 && (
            <div className="mt-10 pt-6 border-t border-zinc-200">
              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
                Demo accounts — click to fill
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {accounts.map((a) => (
                  <button
                    key={a.username}
                    type="button"
                    onClick={() => quickFill(a)}
                    className="text-left p-2.5 rounded-md border border-zinc-200 hover:bg-zinc-50 transition-colors"
                    data-testid={`demo-account-${a.username}`}
                  >
                    <div className="text-xs font-semibold text-zinc-900">{a.label}</div>
                    <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                      {a.username} / {a.password}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <a
            href="/track"
            className="block mt-6 text-center text-[11px] text-zinc-500 hover:text-zinc-900"
            data-testid="customer-tracking-link"
          >
            Looking for your customer tracking page? <span className="underline font-semibold">Open it →</span>
          </a>

          <div className="mt-8 text-center text-[10px] text-zinc-400 uppercase tracking-widest">
            Flowline · Powered by{" "}
            <a href="https://soubor-apps.com" target="_blank" rel="noreferrer" className="text-zinc-600 hover:text-zinc-900 font-semibold">
              Soubor
            </a>{" "}
            · <span className="font-mono normal-case tracking-normal">flowline.one</span>
          </div>
        </div>
      </div>

      {/* Right — image */}
      <div className="hidden lg:block relative">
        <img src={LOGIN_BG} alt="Warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/70 via-zinc-900/30 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="text-[10px] uppercase tracking-[0.2em] mb-2 text-white/70">
            From workshop to doorstep
          </div>
          <div className="font-heading text-3xl font-extrabold tracking-tighter max-w-md leading-tight">
            Every bespoke order, every handover, every customer update — flowing through one place.
          </div>
        </div>
      </div>
    </div>
  );
}
